/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import type Maybe from 'graphql/tsutils/Maybe';

import { isUserRightToLeft } from 'js/lib/language';

import type { Theme } from '@coursera/cds-core';
import { Grid, PageGridContainer, Typography, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { breakPoint, color } from '@coursera/coursera-ui';
import { SvgChevronLeft, SvgChevronRight } from '@coursera/coursera-ui/svg';

import Markdown from 'bundles/cms/components/Markdown';
import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import type { TestimonialsFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import { TESTIMONIALS_CARD_WIDTH } from 'bundles/front-page/common/constants';
import SocialProofItem from 'bundles/front-page/components/modules/SocialProofItem';
import Slider from 'bundles/front-page/components/modules/slider/Slider';
import SocialProofCardContent from 'bundles/front-page/components/modules/slider/SocialProofCardContent';
import { getLOHPTestimonialsHeader, getSocialProofs } from 'bundles/front-page/constants/defaults';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/front-page';

const styles = {
  socialProofContainer: () => css`
    display: block;
    margin-top: 100px;
    position: relative;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    ${breakpoints.down(375)} {
      .rc-Carousel {
        .slider-button.slider-left {
          left: auto;
        }

        .slider-button.slider-right {
          right: auto;
        }
      }
    }
    ${breakpoints.between(376, 608)} {
      .rc-Carousel {
        .slider-button.slider-left {
          left: 0;
        }

        .slider-button.slider-right {
          right: 0;
          left: auto;
        }
      }
    }

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-800) 0 var(--cds-spacing-800);
    }
  `,
  text: css`
    margin: 0 auto 50px;
    max-width: 800px;
    text-align: center;
    width: 100%;
  `,
  title: () => css`
    font-weight: 600;
    font-family: 'Source Sans Pro', Arial, sans-serif;
    color: var(--cds-color-grey-975);
    font-size: 44px;
    line-height: 52px;
    margin: 10px 20px 14px;
    ${breakpoints.down('sm')} {
      font-size: 36px;
      line-height: 48px;
    }
    ${breakpoints.down('xs')} {
      font-size: 28px;
      line-height: 36px;
    }
  `,
  subtitle: () => css`
    color: var(--cds-color-grey-975);
    font-size: 28px;
    line-height: 36px;
    ${breakpoints.down('sm')} {
      font-size: 28px;
      line-height: 36px;
    }
    ${breakpoints.down('xs')} {
      font-size: 16px;
      line-height: 21px;
    }
  `,
  tesimonialsSliderPageGridContainer: css`
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50) !important;
    }
  `,
  textContainer: css`
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-200);
    }
  `,
  mobileText: css`
    ${breakpoints.down('xs')} {
      margin: 0 auto var(--cds-spacing-300);

      .rc-Markdown {
        h2 {
          ${typography2.titleSmall}
          margin-bottom: var(--cds-spacing-100);
        }

        p {
          ${typography2.bodySecondary}
        }
      }
    }
  `,
};

const overrideStyles = {
  markdown: (theme: Theme) => css`
    ${css(styles.text)}
    .rc-Markdown {
      h2 {
        ${theme.typography.d1semibold}
        ${css(styles.title())}
      }

      p {
        ${theme.typography.d2}
        ${css(styles.subtitle())}
      }
    }
  `,
};

export const slidesToShowDefault = 3;
const slidesToShowXl = 3;
const slidesToShowLg = 2;
const slidesToShowMd = 1;
const slidesToShowSm = 1;
const slidesAvailable = getSocialProofs().length;

export const ArrowButton = ({ isArrowRight, ...rest }: { isArrowRight?: boolean }) => {
  const classes = classNames('slider-button', {
    'slider-left': !isArrowRight,
    'slider-right': isArrowRight,
  });
  const isRTL = isUserRightToLeft();
  const nextSlideTitle = _t('Next slide');
  const prevSlideTitle = _t('Previous slide');

  return (
    <div className={classes}>
      <TrackedButton trackingName={isArrowRight ? 'social_proof_arrow_right' : 'social_proof_arrow_left'} {...rest}>
        {isArrowRight ? (
          <SvgChevronRight
            title={isRTL ? prevSlideTitle : nextSlideTitle}
            size={48}
            color="#0056D2"
            hoverColor={color.primaryText}
          />
        ) : (
          <SvgChevronLeft
            title={isRTL ? nextSlideTitle : prevSlideTitle}
            size={48}
            color="#0056D2"
            hoverColor={color.primaryText}
          />
        )}
      </TrackedButton>
    </div>
  );
};

const CAROUSEL_SETTINGS: CarouselSettings = {
  infinite: true,
  nextArrow: <ArrowButton isArrowRight />,
  prevArrow: <ArrowButton />,
  speed: 300,
  slidesToShow: slidesToShowDefault,
  slidesToScroll:
    slidesAvailable % slidesToShowDefault === 0 ? slidesToShowDefault : slidesAvailable % slidesToShowDefault,
  dots: false,
  initialSlide: 0,
  lazyLoad: 'ondemand',
  responsive: [
    {
      breakpoint: breakPoint.xl,
      settings: {
        slidesToShow: slidesToShowXl,
        slidesToScroll: slidesAvailable % slidesToShowXl === 0 ? slidesToShowXl : slidesAvailable % slidesToShowXl,
      },
    },
    {
      breakpoint: breakPoint.lg,
      settings: {
        slidesToShow: slidesToShowLg,
        slidesToScroll: slidesAvailable % slidesToShowLg === 0 ? slidesToShowLg : slidesAvailable % slidesToShowLg,
      },
    },
    {
      breakpoint: breakPoint.md,
      settings: {
        slidesToShow: slidesToShowMd,
        slidesToScroll: slidesAvailable % slidesToShowMd === 0 ? slidesToShowMd : slidesAvailable % slidesToShowMd,
      },
    },
    {
      breakpoint: breakPoint.sm,
      settings: {
        slidesToShow: slidesToShowSm,
        slidesToScroll: slidesAvailable % slidesToShowSm === 0 ? slidesToShowSm : slidesAvailable % slidesToShowSm,
      },
    },
  ],
};

type Props = {
  testimonials: TestimonialsFieldsFragment[];
  title?: Maybe<string>;
};

const renderSocialProofItems = (testimonials: Props['testimonials']) => {
  return testimonials.map((testimonial) => {
    const { authorName, authorTitle, authorOrganization, text, image } = testimonial;

    if (!authorName || !text || !image || !image.url) {
      return null;
    }

    return (
      <SocialProofItem
        key={authorName}
        name={authorName}
        title={authorTitle ?? ''}
        subtitle={authorOrganization ?? ''}
        body={text}
        rebrandImageUrl={image && image.url}
      />
    );
  });
};

const SocialProofContainerCMS: React.FunctionComponent<Props> = ({ title, testimonials }) => {
  const isMobileDeviceWidth = useMediaQuery(breakpoints.down('xs'));

  return (
    <section className="rc-SocialProofContainerRedesign" css={styles.socialProofContainer}>
      <PageGridContainer css={styles.tesimonialsSliderPageGridContainer}>
        <Grid item xs={12}>
          <div css={styles.textContainer}>
            {title ? (
              <div css={[overrideStyles.markdown, styles.mobileText]}>
                <Markdown source={title} />
              </div>
            ) : (
              <div css={[styles.text, styles.mobileText]}>
                <Typography variant="d1semibold" component="h2" css={styles.title}>
                  {getLOHPTestimonialsHeader().title}
                </Typography>
                <Typography variant="d2" component="p" css={styles.subtitle}>
                  {getLOHPTestimonialsHeader().subtitle}
                </Typography>
              </div>
            )}
          </div>
          {isMobileDeviceWidth ? (
            <Slider forcedCardWidth={TESTIMONIALS_CARD_WIDTH}>
              {testimonials.map((testimonial) => (
                <SocialProofCardContent
                  key={testimonial.authorName}
                  authorName={testimonial.authorName}
                  text={testimonial.text}
                  authorOrganization={testimonial.authorOrganization}
                  authorTitle={testimonial.authorTitle}
                  imageUrl={testimonial.image?.url}
                />
              ))}
            </Slider>
          ) : (
            <div className="carousel-container">
              <Carousel settings={CAROUSEL_SETTINGS}>{renderSocialProofItems(testimonials)}</Carousel>
            </div>
          )}
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default SocialProofContainerCMS;
